// Job-board filter model (#39). Filtering, sorting and pagination all happen
// SERVER-SIDE now (go.nexilink SearchActiveNeedsPublic) — this module only defines
// the shared filter shape used by the filter bar (JobFilterBar), its persistence
// (jobBoardPrefs) and the query the page sends to the backend. The old client-side
// match/sort helpers were removed when the board moved to backend search.

export type JobSort = 'recent' | 'deadline';

export interface JobFilters {
    city: string;             // '' = all
    employmentType: string;   // '' = all (EmploymentType value)
    extent: string;           // '' = all (EmploymentExtent value)
    workLocation: string;     // '' = all | 'onsite' (offers on-site) | 'remote' (offers remote)
    competencies: string[];   // OR within facet; [] = all
    supervisionOnly: boolean; // true = only needs offering supervision
}

export const EMPTY_JOB_FILTERS: JobFilters = {
    city: '',
    employmentType: '',
    extent: '',
    workLocation: '',
    competencies: [],
    supervisionOnly: false,
};

export function jobFiltersActive(f: JobFilters): boolean {
    return (
        !!f.city ||
        !!f.employmentType ||
        !!f.extent ||
        !!f.workLocation ||
        f.competencies.length > 0 ||
        f.supervisionOnly
    );
}

// Structured saved-search criteria (#39 Phase 2) — the backend wire shape for a
// "Bevakning". Mirrors JobFilters but uses the API's snake_case field names, and
// omits empty facets. A saved search is this + an optional free-text query.
export interface JobSearchCriteria {
    city?: string;
    employment_type?: string;
    extent?: string;
    work_location?: string;
    competencies?: string[];
    supervision?: boolean;
}

// filtersToCriteria projects the live filter-bar state onto the saved-search
// wire shape, dropping unset facets.
export function filtersToCriteria(f: JobFilters): JobSearchCriteria {
    const c: JobSearchCriteria = {};
    if (f.city) c.city = f.city;
    if (f.employmentType) c.employment_type = f.employmentType;
    if (f.extent) c.extent = f.extent;
    if (f.workLocation) c.work_location = f.workLocation;
    if (f.competencies.length) c.competencies = f.competencies;
    if (f.supervisionOnly) c.supervision = true;
    return c;
}

// criteriaToFilters is the inverse — used to re-apply a saved search back into
// the filter bar ("click a bevakning to load it").
export function criteriaToFilters(c?: JobSearchCriteria | null): JobFilters {
    return {
        city: c?.city ?? '',
        employmentType: c?.employment_type ?? '',
        extent: c?.extent ?? '',
        workLocation: c?.work_location ?? '',
        competencies: c?.competencies ?? [],
        supervisionOnly: !!c?.supervision,
    };
}

// criteriaChips projects a saved search (free-text + criteria) onto a flat list
// of human-readable pills for display in the bevakningar UI, in the same
// vocabulary the filter bar uses. Empty everywhere → a single "Alla nya annonser"
// pill. Imports the labels lazily to avoid a circular dependency with models/need.
export function criteriaChips(query: string, c?: JobSearchCriteria | null): string[] {
    const chips: string[] = [];
    const q = query.trim();
    if (q) chips.push(`”${q}”`);
    if (c?.city) chips.push(c.city);
    if (c?.employment_type) chips.push(EMPLOYMENT_TYPE_LABELS[c.employment_type] ?? c.employment_type);
    if (c?.extent) chips.push(EXTENT_LABELS[c.extent] ?? c.extent);
    if (c?.work_location === 'onsite') chips.push('På plats');
    if (c?.work_location === 'remote') chips.push('Distans');
    (c?.competencies ?? []).forEach((comp) => chips.push(comp));
    if (c?.supervision) chips.push('Handledning erbjuds');
    if (chips.length === 0) chips.push('Alla nya annonser');
    return chips;
}

// deriveBevakningName suggests a default name for a new bevakning — the same
// summary the chips render, joined with " · " (mirrors the backend Label()).
export function deriveBevakningName(query: string, c?: JobSearchCriteria | null): string {
    const chips = criteriaChips(query, c).map((s) => s.replace(/^”|”$/g, ''));
    return chips.join(' · ');
}

// Local label maps for the two enum facets, kept here so criteriaChips has no
// import cycle. Values must stay in sync with models/need's *Labels.
const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
    permanent: 'Tillsvidare',
    temporary: 'Visstid',
    substitute: 'Vikariat',
    probationary: 'Provanställning',
    hourly: 'Timanställning',
};
const EXTENT_LABELS: Record<string, string> = {
    full_time: 'Heltid',
    part_time: 'Deltid',
};

// searchSignature is a canonical fingerprint of (free-text + criteria) so the UI
// can tell whether the current search is already saved — regardless of competency
// ordering. Computed the same way on both sides of the comparison (mirrors the
// backend's SignatureKey conceptually; only needs to be internally consistent).
export function searchSignature(query: string, c: JobSearchCriteria): string {
    const q = query.trim().toLowerCase();
    const comps = [...(c.competencies ?? [])].sort();
    const canon = {
        city: c.city ?? '',
        employment_type: c.employment_type ?? '',
        extent: c.extent ?? '',
        work_location: c.work_location ?? '',
        competencies: comps,
        supervision: !!c.supervision,
    };
    const hasFacet =
        !!canon.city || !!canon.employment_type || !!canon.extent ||
        !!canon.work_location || comps.length > 0 || canon.supervision;
    return `${q}|${hasFacet ? JSON.stringify(canon) : ''}`;
}
